import React, { useRef,  useState, useEffect } from 'react';
import Topbar from '../components/Topbar';
import {
  getSecurityCheckpoints,
  createSecurityCheckpoint,
  updateSecurityCheckpoint,
  getSingleSecurityCheckpoint,
  deleteSecurityCheckpoint,
  getSecurityMetaData,
  getSecurityConcourses
} from '../services/securityCheckpointService';
import { toast } from 'react-toastify';
import Spinner from '../components/Spinner';
import { useRefresh } from '../utils/RefreshContext';

const Svg = ({ d, ...props }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="1.8" strokeLinecap="round" {...props}>
    <path d={d} />
  </svg>
);

const SecurityCheckpoints = () => {

  const { refreshKey } = useRefresh();
  const [checkpoints, setCheckpoints] = useState([]);
  const [airports, setAirports] = useState([]);
  const [laneTypes, setLaneTypes] = useState([]);
  const [concourses, setConcourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingConcourses, setLoadingConcourses] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Custom Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Pagination & Filter States
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [filterAirport, setFilterAirport] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    airport_id: '',
    concourse_id: '',
    concourse_name: '',
    lng: '',
    lat: '',
    status: 1,
    notes: '',
    lane_ids: []
  });

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await getSecurityMetaData();
        if (res?.data) {
          setAirports(res.data.airports || []);
          setLaneTypes(res.data.laneTypes || []);
        }
      } catch (err) { console.error(err); }
    };
    fetchMeta();
  }, []);
  const prevSearch = useRef(search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== search;
    prevSearch.current = search;

    const timer = setTimeout(() => {
      if (searchChanged && page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    }, searchChanged ? 500 : 0);

    return () => clearTimeout(timer);
  }, [page, limit, filterAirport, filterStatus, refreshKey, search]);


  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search || undefined,
        airport_id: filterAirport === 'All' ? undefined : filterAirport,
        status: filterStatus === 'All' ? undefined : filterStatus
      };
      const res = await getSecurityCheckpoints(params);
      setCheckpoints(res?.data || []);
      setTotalPages(res?.meta?.totalPages || 1);
      setTotalRecords(res?.meta?.total || 0);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error("Failed to load checkpoints");
    } finally {
      setLoading(false);
    }
  };

  const handleAirportChange = async (airportId) => {
    setFormData({ ...formData, airport_id: airportId, concourse_id: '' });
    setConcourses([]);
    if (!airportId) return;

    setLoadingConcourses(true);
    try {
      const res = await getSecurityConcourses(airportId);
      setConcourses(res?.data || []);
    } catch (err) { 
      console.error(err); 
    } finally {
      setLoadingConcourses(false);
    }
  };

  const handleLaneTypeToggle = (id) => {
    const current = [...formData.lane_ids];
    if (current.includes(id)) {
      setFormData({ ...formData, lane_ids: current.filter(item => item !== id) });
    } else {
      setFormData({ ...formData, lane_ids: [...current, id] });
    }
  };

  const handleEdit = async (cp) => {
    setLoading(true);
    setCurrentId(cp.id);
    setIsEditing(true);
    
    try {
      const res = await getSingleSecurityCheckpoint(cp.id);
      const data = res?.data;
      
      if (data) {
        // Fetch concourses for this airport first
        if (data.airport_id) {
          const concRes = await getSecurityConcourses(data.airport_id);
          setConcourses(concRes?.data || []);
        }

        setFormData({
          name: data.name || '',
          airport_id: data.airport_id || '',
          concourse_id: data.concourse_id || '',
          concourse_name: data.concourse?.name || '',
          lng: data.lng || '',
          lat: data.lat || '',
          status: Number(data.status) || 1,
          notes: data.notes || '',
          lane_ids: data.lanes?.map(lt => lt.id) || []
        });
        
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Edit fetch error:', error);
      toast.error("Failed to fetch checkpoint details");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (cp) => {
    setDeleteTarget(cp);
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSecurityCheckpoint(deleteTarget.id);
      toast.success("Checkpoint deleted successfully");
      setShowConfirm(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.airport_id || !formData.concourse_id || !formData.lng || !formData.lat) {
      return toast.warning("Please fill required fields");
    }

    setSubmitting(true);
    try {
      const response = isEditing
        ? await updateSecurityCheckpoint(currentId, formData)
        : await createSecurityCheckpoint(formData);

      if (response && (response.status === 200 || response.status === 201)) {
        toast.success(`Checkpoint ${isEditing ? 'updated' : 'created'} successfully`);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error(error);
      // toast.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      airport_id: '',
      concourse_id: '',
      concourse_name: '',
      lng: '',
      lat: '',
      status: 1,
      notes: '',
      lane_ids: []
    });
    setConcourses([]);
    setIsEditing(false);
    setCurrentId(null);
    setShowForm(false);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const laneColors = {
    'TSA Standard': ['#FFF0F0', '#E53E3E'],
    'TSA PreCheck': ['#EAFAF3', '#1A7A42'],
    'CLEAR': ['#EEF6FF', 'var(--blue)'],
    'Global Entry': ['#FFFBE6', '#B8860B']
  };

  if (loading && checkpoints.length === 0) return <Spinner />;

  return (
    <div>
      <Topbar title="Security Checkpoint Management" subtitle="Monitor and manage airport security checkpoints and lane types" />

      {/* CUSTOM CONFIRMATION MODAL */}
      {showConfirm && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, backdropFilter: 'blur(4px)' }}>
          <div className="modal-card" style={{ background: 'white', padding: 32, borderRadius: 20, width: '100%', maxWidth: 400, textAlign: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 64, height: 64, background: '#FFF5F5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2"><path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)', marginBottom: 10 }}>Confirm Deletion</h3>
            <p style={{ fontSize: 14, color: 'var(--grey)', marginBottom: 24, lineHeight: 1.5 }}>
              Are you sure you want to delete this checkpoint? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1, background: '#F87171', borderColor: '#F87171' }} onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="content">
        <div className="screen-label">5.3 — Security Checkpoint Management</div>

        <div className="card">
          {/* FORM SECTION */}
          {(showForm || isEditing) && (
            <div style={{ borderBottom: '2px solid #F0F4F8', padding: '24px 26px', background: '#F8FAFC', borderRadius: '20px 20px 0 0' }}>
              <div className="screen-label" style={{ marginBottom: 18, fontSize: 13, color: 'var(--blue)', fontWeight: 700 }}>{isEditing ? 'Modify Existing Checkpoint' : 'Add New Security Checkpoint'}</div>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group full">
                    <label className="form-label">Checkpoint Name <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder='e.g. "Concourse A Security"'
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Airport <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.airport_id}
                      onChange={e => handleAirportChange(e.target.value)}
                    >
                      <option value="">Select Airport</option>
                      {airports.map(ap => (
                        <option key={ap.id} value={ap.id}>{ap.code} — {ap.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Concourse / Zone <span className="req">*</span></label>
                    {isEditing ? (
                      <input 
                        className="form-input" 
                        value={formData.concourse_name} 
                        readOnly 
                        style={{ background: '#F8FAFC', cursor: 'not-allowed' }}
                      />
                    ) : (
                      <select
                        className="form-select"
                        value={formData.concourse_id}
                        onChange={e => setFormData({ ...formData, concourse_id: e.target.value })}
                        disabled={!formData.airport_id || loadingConcourses}
                      >
                        <option value="">{loadingConcourses ? 'Loading concourses...' : 'Select Concourse'}</option>
                        {concourses.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Longitude <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder="e.g. -82.88550"
                      value={formData.lng}
                      onChange={e => setFormData({ ...formData, lng: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Latitude <span className="req">*</span></label>
                    <input
                      className="form-input"
                      placeholder="e.g. 39.99690"
                      value={formData.lat}
                      onChange={e => setFormData({ ...formData, lat: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status <span className="req">*</span></label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: Number(e.target.value) })}
                    >
                      <option value={1}>Active</option>
                      <option value={2}>Inactive</option>
                    </select>
                  </div>
                  <div className="form-group full">
                    <label className="form-label">Notes <span className="opt">Optional</span></label>
                    <input
                      className="form-input"
                      placeholder='e.g. "Opens at 4:30 AM on weekdays"'
                      value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>
                </div>

                <div className="fsec-title">Available Lane Types — Select all that apply</div>
                <div className="lanes-grid">
                  {laneTypes.map((l) => (
                    <label key={l.id} className={`lane-item${formData.lane_ids.includes(l.id) ? ' checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={formData.lane_ids.includes(l.id)}
                        onChange={() => handleLaneTypeToggle(l.id)}
                      />
                      <div>
                        <div className="lane-name">
                          {l.name} {l.is_required ? <span style={{ color: 'var(--red)', fontSize: 11, marginLeft: 4 }}>Required</span> : <span style={{ color: 'var(--grey)', fontSize: 11, marginLeft: 4 }}>Optional</span>}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="form-footer" style={{ paddingLeft: 0, paddingRight: 0, marginTop: 24 }}>
                  <button type="button" className="btn btn-outline" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /></svg>
                    {submitting ? 'Saving...' : isEditing ? 'Update Checkpoint' : 'Save Checkpoint'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="toolbar">
            <div className="search-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--grey)" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              <input
                className="search-input"
                placeholder="Search checkpoints..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={filterAirport}
              onChange={e => setFilterAirport(e.target.value)}
            >
              <option value="All">All Airports</option>
              {airports.map(ap => (
                <option key={ap.id} value={ap.id}>{ap.code}</option>
              ))}
            </select>
            <select
              className="filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="All">All Statuses</option>
              <option value={1}>Active</option>
              <option value={2}>Inactive</option>
            </select>
            {!showForm && (
              <button className="btn btn-primary" onClick={() => setShowForm(true)}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>Add Checkpoint</button>
            )}
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>Checkpoint Name</th>
                <th>Airport</th>
                <th>Concourse</th>
                <th>Coordinates</th>
                <th>Available Lanes</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}><Spinner /></td></tr>
              ) : checkpoints.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: 'var(--grey)' }}>No checkpoints found</td></tr>
              ) : (
                checkpoints.map((cp) => (
                  <tr key={cp.id}>
                    <td style={{ fontWeight: 600, color: 'var(--navy)' }}>{cp.name}</td>
                    <td><span className="tag">{cp.airport?.code}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--grey)' }}>{cp.concourse?.name}</td>
                    <td style={{ fontSize: 11, color: 'var(--grey)', fontFamily: "'Space Grotesk', sans-serif" }}>
                      {cp.lat}, {cp.lng}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {cp.lanes?.map(lt => (
                          <span key={lt.id} style={{
                            background: laneColors[lt.name]?.[0] || '#F0F0F0',
                            color: laneColors[lt.name]?.[1] || 'var(--grey)',
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '2px 7px',
                            borderRadius: 5
                          }}>
                            {lt.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td><span className={`badge badge-${Number(cp.status) === 1 ? 'active' : 'inactive'}`}>{Number(cp.status) === 1 ? 'Active' : 'Inactive'}</span></td>
                    <td>
                      <div className="action-btns">
                        <button className="action-btn" onClick={() => handleEdit(cp)}><Svg d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></button>
                        <button className="action-btn danger" onClick={() => confirmDelete(cp)}><Svg d="M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          {checkpoints.length > 0 && (
            <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 26px', borderTop: '1px solid #F0F4F8' }}>
              <div style={{ fontSize: 13, color: 'var(--grey)' }}>
                Showing <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{((page - 1) * limit) + 1}</span> to <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{Math.min(page * limit, totalRecords)}</span> of <span style={{ fontWeight: 700, color: 'var(--dark)' }}>{totalRecords}</span> entries
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-outline"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', gap: 4 }}>
                  {renderPagination().map((p, i) => (
                    <button
                      key={i}
                      className={`btn ${page === p ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => typeof p === 'number' && setPage(p)}
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                        minWidth: 36,
                        cursor: typeof p === 'number' ? 'pointer' : 'default',
                        border: typeof p === 'number' ? '' : 'none',
                        background: typeof p === 'number' ? '' : 'transparent'
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                  style={{ padding: '6px 12px', fontSize: 12 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SecurityCheckpoints;
